import { useState } from "react";
import { ArrowLeft, KeyRound } from "lucide-react";
import { activateHotelCode } from "./hotelClient";
import RequestMoreDevicesModal from "./RequestMoreDevicesModal";

// Sector-specific copy for the activation screen. Choose based on which
// sector tile the visitor came from on the Organisation Sign In page.
const SECTOR_ACTIVATE_COPY = {
  hotel: {
    modeName: "Hotel Mode",
    orgLower: "hotel",
    codePrefix: "HTL",
    codePlaceholder: "HTL-XXXX-XXXX",
    formatLine: "Format:",
    formatCode: "HTL-XXXX-XXXX",
    deviceLabel: "Device name",
    devicePlaceholder: "e.g. Main Kids' Club — Device 1",
    deviceHelper: "Helps you and Poodle Talk identify this device in the manager dashboard.",
    infoTitle: "One code can activate every device in your kids' club.",
    infoBody: 'Repeat this screen on each device with a unique device name — e.g. "Main Kids\' Club — Device 1", "Reception Tablet", "Quiet Room Tablet". All devices will share the same central credit balance.',
    footNote: "This device will stay activated until your hotel account, activation code or this specific device is deactivated by Poodle Talk.",
    codeMissingErr: "Please enter your activation code.",
    hintMissingErr: 'Please give this device a name so it can be identified (e.g. "Main Kids\' Club — Device 1").',
    unknownErr: "We couldn't activate this code.",
    wrongPrefixErr: "This looks like a School or Charity code. Please go back and pick the correct organisation type.",
  },
  school: {
    modeName: "School Mode",
    orgLower: "school",
    codePrefix: "SCH",
    codePlaceholder: "SCH-XXXX-XXXX",
    formatLine: "Format:",
    formatCode: "SCH-XXXX-XXXX",
    deviceLabel: "Device name",
    devicePlaceholder: "e.g. Main Classroom — Device 1",
    deviceHelper: "Helps you and Poodle Talk identify this device in the school dashboard.",
    infoTitle: "One code can activate every device in your classroom.",
    infoBody: 'Repeat this screen on each device with a unique device name — e.g. "Main Classroom — Device 1", "SEND Room Tablet", "Library Tablet". All devices will share the same central credit balance.',
    footNote: "This device will stay activated until your school account, activation code or this specific device is deactivated by Poodle Talk.",
    codeMissingErr: "Please enter your activation code.",
    hintMissingErr: 'Please give this device a name so it can be identified (e.g. "Main Classroom — Device 1").',
    unknownErr: "We couldn't activate this code.",
    wrongPrefixErr: "This looks like a Hotel or Charity code. Please go back and pick the correct organisation type.",
  },
  charity: {
    modeName: "Charity Mode",
    orgLower: "charity",
    codePrefix: "CHR",
    codePlaceholder: "CHR-XXXX-XXXX",
    formatLine: "Format:",
    formatCode: "CHR-XXXX-XXXX",
    deviceLabel: "Device name",
    devicePlaceholder: "e.g. Main Activity Room — Device 1",
    deviceHelper: "Helps you and Poodle Talk identify this device in the charity dashboard.",
    infoTitle: "One code can activate every device at your charity.",
    infoBody: 'Repeat this screen on each device with a unique device name — e.g. "Main Activity Room — Device 1", "Family Room Tablet", "Community Hall Tablet". All devices will share the same central credit balance.',
    footNote: "This device will stay activated until your charity account, activation code or this specific device is deactivated by Poodle Talk.",
    codeMissingErr: "Please enter your activation code.",
    hintMissingErr: 'Please give this device a name so it can be identified (e.g. "Main Activity Room — Device 1").',
    unknownErr: "We couldn't activate this code.",
    wrongPrefixErr: "This looks like a Hotel or School code. Please go back and pick the correct organisation type.",
  },
};

// Staff (hotel / school / charity) enter their org activation code here.
// Backend validates and mints a device_token bound to this device. The
// backend also enforces that the code prefix matches the sector the user is
// activating for — so a HTL code cannot activate School Mode etc.
export default function HotelActivation({ onBack, onActivated, sector = "hotel" }) {
  const s = ["hotel", "school", "charity"].includes(sector) ? sector : "hotel";
  const copy = SECTOR_ACTIVATE_COPY[s];
  const [code, setCode] = useState("");
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [limitReached, setLimitReached] = useState(null); // {code, expected, active} on 409

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLimitReached(null);
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) { setErr(copy.codeMissingErr); return; }
    // Client-side sector guard — the backend enforces this too, but this
    // gives the user a friendlier message before hitting the network.
    if (/^(HTL|SCH|CHR)-/.test(trimmed) && !trimmed.startsWith(`${copy.codePrefix}-`)) {
      setErr(copy.wrongPrefixErr);
      return;
    }
    if (!hint.trim() || hint.trim().length < 2) {
      setErr(copy.hintMissingErr);
      return;
    }
    setBusy(true);
    try {
      const hotel = await activateHotelCode(trimmed, hint, s);
      onActivated && onActivated(hotel);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "device_limit_reached") {
        setLimitReached({ code: detail.code, expected: detail.expected, active: detail.active });
      } else {
        setErr((typeof detail === "string" && detail) || copy.unknownErr);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <header className="bg-white/80 backdrop-blur-md border-b border-[#E0E0E0] px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={onBack} className="p-2 rounded-full hover:bg-[#F3F0E6] transition-colors" data-testid="hotel-activate-back">
            <ArrowLeft className="w-6 h-6 text-[#4A4A4A]" />
          </button>
          <h1 className="text-lg sm:text-xl font-semibold text-[#4A4A4A]" data-testid="hotel-activate-title">Activate {copy.modeName}</h1>
        </div>
      </header>

      <main className="flex-1 max-w-md mx-auto w-full px-4 py-8 sm:py-12">
        <div className="text-center mb-8">
          <KeyRound className="w-10 h-10 mx-auto mb-3 text-[#B89823]" />
          <h2 className="text-2xl font-bold text-[#4A4A4A] mb-2">Enter your activation code</h2>
          <p className="text-sm text-[#6B6B6B]" data-testid="hotel-activate-format">
            Your organisation manager received this code by email. {copy.formatLine}{" "}
            <span className="font-mono">{copy.formatCode}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[#4A4A4A] mb-1 uppercase tracking-wide">Activation code</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={13}
              placeholder={copy.codePlaceholder}
              autoFocus
              data-testid="hotel-activation-code-input"
              className="w-full px-4 py-3 rounded-2xl border-2 border-[#E0E0E0] focus:border-[#B89823] outline-none text-center font-mono text-lg tracking-wider"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#4A4A4A] mb-1 uppercase tracking-wide">{copy.deviceLabel} *</label>
            <input
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              maxLength={80}
              placeholder={copy.devicePlaceholder}
              required
              data-testid="hotel-activation-device-hint"
              className="w-full px-4 py-3 rounded-2xl border-2 border-[#E0E0E0] focus:border-[#B89823] outline-none text-sm"
            />
            <p className="text-[11px] text-[#9A9A9A] mt-1">{copy.deviceHelper}</p>
          </div>
          {err && <div className="bg-[#FFF0F0] border border-[#F5D0D0] rounded-xl p-3 text-sm text-[#A55B64] text-center" data-testid="hotel-activation-error">{err}</div>}
          {limitReached && (
            <div className="bg-[#FFF7D6] border border-[#E8D4A8] rounded-xl p-3 space-y-2" data-testid="hotel-activation-limit-reached">
              <p className="text-sm text-[#5A4A32]"><strong>{limitReached.code}</strong> has reached its approved device limit ({limitReached.active} of {limitReached.expected} devices already active).</p>
              <button
                type="button"
                onClick={() => setLimitReached({ ...limitReached, open: true })}
                data-testid="hotel-activation-request-more-devices"
                className="w-full px-3 py-2 bg-[#4A4A4A] text-white rounded-full font-semibold text-sm hover:bg-black"
              >
                Request more devices
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !code || !hint.trim()}
            data-testid="hotel-activation-submit"
            className="w-full px-4 py-3 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold hover:bg-[#D9C599] transition-all disabled:opacity-50"
          >
            {busy ? "Activating…" : `Activate ${copy.modeName}`}
          </button>
        </form>

        <div className="mt-6 bg-[#FFF7D6] border border-[#E8D4A8] rounded-2xl p-3 text-[11px] text-[#5A4A32]" data-testid="hotel-activate-info">
          <p className="font-semibold mb-1">{copy.infoTitle}</p>
          <p>{copy.infoBody}</p>
        </div>

        <p className="text-xs text-[#9A9A9A] text-center mt-6" data-testid="hotel-activate-footnote">
          {copy.footNote}
        </p>
      </main>

      {limitReached?.open && (
        <RequestMoreDevicesModal
          code={limitReached.code}
          sector={s}
          onClose={() => setLimitReached(null)}
          onSuccess={() => { /* modal shows its own done screen; user retries via button */ }}
        />
      )}
    </div>
  );
}
